import * as React from 'react';
import Checkbox from 'antd/lib/checkbox';
import Flex from 'antd/lib/flex';
import InputNumber from 'antd/lib/input-number';
import Text from 'antd/lib/typography/Text';
import { MINUTES_PER_HOUR } from '@/timeHelpers';

const MAX_DELAY_AFTER_ACCELERATION_MINUTES = 24 * MINUTES_PER_HOUR;
const DEFAULT_DELAY_MINUTES = MINUTES_PER_HOUR;

interface DelayAfterAccelerationInputProps {
    label: React.ReactNode;
    value?: number; // minutes, 0 = no delay
    onChange?: (minutes: number) => void;
    disabled?: boolean;
    maxMinutes?: number;
}

export const DelayAfterAccelerationInput: React.FC<DelayAfterAccelerationInputProps> = ({
    label,
    value = 0,
    onChange,
    disabled,
    maxMinutes = MAX_DELAY_AFTER_ACCELERATION_MINUTES,
}) => {
    // Kept apart from the value so clearing both inputs while editing doesn't collapse them.
    const [isEnabled, setIsEnabled] = React.useState(value > 0);
    React.useEffect(() => {
        if (value > 0) {
            setIsEnabled(true);
        }
    }, [value]);

    const hours = Math.floor(value / MINUTES_PER_HOUR);
    const minutes = value % MINUTES_PER_HOUR;
    const maxHours = Math.floor(maxMinutes / MINUTES_PER_HOUR);
    const setDelay = (newHours: number, newMinutes: number) =>
        onChange?.(Math.min(newHours * MINUTES_PER_HOUR + newMinutes, maxMinutes));

    return (
        <Flex vertical gap={8}>
            <Checkbox
                checked={isEnabled}
                disabled={disabled}
                onChange={(event) => {
                    setIsEnabled(event.target.checked);
                    onChange?.(event.target.checked ? DEFAULT_DELAY_MINUTES : 0);
                }}
            >
                {label}
            </Checkbox>
            {isEnabled && (
                <Flex align="center" gap={8} style={{ marginLeft: 24 }}>
                    <InputNumber
                        min={0}
                        max={maxHours}
                        precision={0}
                        value={hours}
                        disabled={disabled}
                        style={{ width: 90 }}
                        suffix={<Text type="secondary">h</Text>}
                        onChange={(newHours) => setDelay(newHours ?? 0, minutes)}
                    />
                    <InputNumber
                        min={0}
                        max={MINUTES_PER_HOUR - 1}
                        precision={0}
                        value={minutes}
                        disabled={disabled}
                        style={{ width: 90 }}
                        suffix={<Text type="secondary">m</Text>}
                        onChange={(newMinutes) => setDelay(hours, newMinutes ?? 0)}
                    />
                    <Text type="secondary">Max {maxHours}h</Text>
                </Flex>
            )}
        </Flex>
    );
};
